import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Mail, Phone, Camera, Upload, Trash2, Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordCard from '../../components/ChangePasswordCard';
import { resolveUploadUrl } from '../../utils/media';

const ProfilePage = ({ onMenuClick }) => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [phone, setPhone] = useState('');
  const [originalPhone, setOriginalPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [avatarCacheKey, setAvatarCacheKey] = useState(Date.now());

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await api.get('/student/profile');
        setProfile(response.data);
        setPhone(response.data.phone || '');
        setOriginalPhone(response.data.phone || '');
        setAvatarUrl(response.data.avatar_url || user?.avatar_url || null);
      } catch (error) {
        setProfile(null);
        setAvatarUrl(user?.avatar_url || null);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Maximum 5 Mo.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/auth/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAvatarUrl(response.data.avatar_url);
      setAvatarBroken(false);
      setAvatarCacheKey(Date.now());
      if (updateUser) {
        updateUser({ avatar_url: response.data.avatar_url });
      }
      toast.success('Photo de profil mise à jour');
    } catch (error) {
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    if (!avatarUrl) return;
    if (!window.confirm('Voulez-vous supprimer votre photo de profil ?')) return;

    setUploadingAvatar(true);
    try {
      await api.delete('/auth/avatar');
      setAvatarUrl(null);
      setAvatarBroken(false);
      if (updateUser) {
        updateUser({ avatar_url: null });
      }
      toast.success('Photo de profil supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    const trimmedPhone = phone.trim();
    if (trimmedPhone === originalPhone.trim()) {
      toast.info('Aucune modification détectée');
      return;
    }
    if (trimmedPhone && (trimmedPhone.length < 10 || trimmedPhone.length > 15)) {
      toast.error('Le numéro de téléphone doit contenir entre 10 et 15 chiffres.');
      return;
    }
    setSaving(true);
    try {
      await api.put('/auth/profile', null, { params: { phone: trimmedPhone } });
      setPhone(trimmedPhone);
      setOriginalPhone(trimmedPhone);
      updateUser({ phone: trimmedPhone });
      toast.success('Téléphone mis à jour avec succès');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const getAvatarSrc = () => {
    const src = resolveUploadUrl(avatarUrl);
    if (!src) return null;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}t=${avatarCacheKey}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Mon Profil" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-violet-600" />
        </div>
      </div>
    );
  }

  const p = profile || {};

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mon Profil" onMenuClick={onMenuClick} />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Avatar & identity header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="relative group">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="hidden"
                />
                <div
                  className="w-28 h-28 rounded-full overflow-hidden cursor-pointer ring-4 ring-violet-100 dark:ring-violet-900"
                  onClick={handleAvatarClick}
                >
                  {avatarUrl && !avatarBroken ? (
                    <img
                      src={getAvatarSrc()}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={() => setAvatarBroken(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                      <span className="text-white font-bold text-3xl">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </span>
                    </div>
                  )}
                  <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    {uploadingAvatar ? (
                      <RefreshCw className="w-8 h-8 text-white animate-spin" />
                    ) : (
                      <Camera className="w-8 h-8 text-white" />
                    )}
                  </div>
                </div>
                <div className="flex justify-center gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={handleAvatarClick} disabled={uploadingAvatar}>
                    <Upload className="w-4 h-4 mr-1" />
                    {avatarUrl ? 'Modifier' : 'Ajouter'}
                  </Button>
                  {avatarUrl && (
                    <Button variant="outline" size="sm" onClick={handleDeleteAvatar} disabled={uploadingAvatar} className="text-red-600 hover:text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div className="text-center md:text-left flex-1">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {p.first_name} {p.last_name}
                </h1>
                <p className="text-slate-500 flex items-center justify-center md:justify-start gap-2 mt-1">
                  <Mail className="w-4 h-4" /> {p.email}
                </p>
                {p.student_number && (
                  <p className="text-sm text-slate-400 mt-1">N° étudiant : {p.student_number}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal information — read-only except phone */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Informations Personnelles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input value={p.first_name || ''} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input value={p.last_name || ''} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                </div>
              </div>

              <div>
                <Label>Email</Label>
                <Input value={p.email || ''} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
              </div>

              {p.date_of_birth && (
                <div>
                  <Label>Date de naissance</Label>
                  <Input value={new Date(p.date_of_birth).toLocaleDateString('fr-FR')} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                </div>
              )}

              {(p.address || p.city) && (
                <div>
                  <Label>Adresse</Label>
                  <Input
                    value={[p.address, p.postal_code, p.city].filter(Boolean).join(', ')}
                    readOnly
                    className="bg-slate-50 dark:bg-slate-800 mt-1"
                  />
                </div>
              )}

              <div>
                <Label className="flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Téléphone
                </Label>
                <Input
                  type="tel"
                  inputMode="numeric"
                  maxLength={15}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                  placeholder="0612345678"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Chiffres uniquement, entre 10 et 15 caractères. Seul le numéro de téléphone est modifiable.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Training information — read-only */}
        {p.course && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Formation</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Formation en cours</Label>
                  <Input value={p.course.name} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                </div>
                {(p.training_start_date || p.training_end_date) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {p.training_start_date && (
                      <div>
                        <Label>Date de début</Label>
                        <Input value={new Date(p.training_start_date).toLocaleDateString('fr-FR')} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                      </div>
                    )}
                    {p.training_end_date && (
                      <div>
                        <Label>Date de fin</Label>
                        <Input value={new Date(p.training_end_date).toLocaleDateString('fr-FR')} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Company information — read-only */}
        {p.company && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Entreprise d'Apprentissage</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label>Entreprise</Label>
                  <Input value={p.company.name || ''} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                </div>
                {p.company.contact_name && (
                  <div>
                    <Label>Maître d'apprentissage</Label>
                    <Input value={p.company.contact_name} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                  </div>
                )}
                {p.company.contact_email && (
                  <div>
                    <Label>Contact email</Label>
                    <Input value={p.company.contact_email} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                  </div>
                )}
                {p.company.contact_phone && (
                  <div>
                    <Label>Contact téléphone</Label>
                    <Input value={p.company.contact_phone} readOnly className="bg-slate-50 dark:bg-slate-800 mt-1" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Change password */}
        <ChangePasswordCard />

        {/* Save — only phone */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Enregistrer</>
            )}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;
